import React, { useState } from 'react';
import { cleanupPwa } from './pwa';

const PWAUninstaller: React.FC = () => {
  const [isConfirming, setIsConfirming] = useState(false);
  const [isUninstalling, setIsUninstalling] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);

  const handleUninstall = async () => {
    setIsUninstalling(true);
    
    try {
      await cleanupPwa();
      setIsComplete(true);
    } catch (error) {
      console.error('Error during PWA cleanup:', error);
      setIsUninstalling(false);
    }
  };

  const handleCancel = () => {
    setIsCancelled(true);
  };
  if (isCancelled) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={styles.title}>❌ Uninstall Cancelled</h2>
          <p style={styles.message}>
            The PWA uninstall process was cancelled. Your PWA data remains intact.
          </p>
          <button 
            style={styles.primaryButton}
            onClick={() => setIsCancelled(false)}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (isComplete) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={styles.title}>✅ PWA Uninstalled</h2>
          <p style={styles.message}>
            The PWA data has been successfully removed from your device.
          </p>
        </div>
      </div>
    );
  }

  if (isUninstalling) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={styles.title}>🔄 Uninstalling PWA</h2>
          <p style={styles.message}>
            Removing PWA data from your device...
          </p>
          <div style={styles.spinner}></div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>🗑️ Uninstall PWA</h2>
        <p style={styles.message}>
          Are you sure you want to remove this PWA and all its data from your device?
        </p>
        <p style={styles.subtitle}>
          This will remove:
        </p>
        <ul style={styles.list}>
          <li>Service worker registrations</li>
          <li>Cached data</li>
          <li>Local storage data</li>
          <li>IndexedDB databases</li>
        </ul>
        
        <div style={styles.buttonContainer}>
          <button 
            style={styles.cancelButton}
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button 
            style={styles.uninstallButton}
            onClick={handleUninstall}
          >
            Uninstall PWA
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    padding: '20px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '40px',
    maxWidth: '500px',
    width: '100%',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    textAlign: 'center' as const,
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '20px',
    color: '#333',
  },
  message: {
    fontSize: '16px',
    lineHeight: '1.5',
    color: '#666',
    marginBottom: '20px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#888',
    marginBottom: '16px',
    fontWeight: '500',
  },
  list: {
    textAlign: 'left' as const,
    fontSize: '14px',
    color: '#666',
    marginBottom: '30px',
    paddingLeft: '20px',
  },
  buttonContainer: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    flexWrap: 'wrap' as const,
  },
  cancelButton: {
    padding: '12px 24px',
    borderRadius: '8px',
    border: '2px solid #ddd',
    backgroundColor: 'white',
    color: '#666',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },  uninstallButton: {
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#dc3545',
    color: 'white',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  primaryButton: {
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#007bff',
    color: 'white',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #007bff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '20px auto',
  },
};

// Add CSS animation for spinner
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);

export default PWAUninstaller;
